// ===========================================
// CART ADDONS - Auto-add addon products to cart
// ===========================================

(function() {
  'use strict';

  // --- Config ---
  const CACHE_KEY = 'addon_metafields_cache';
  const CACHE_TTL = 60000; // 1 min
  const DISMISSED_KEY = 'dismissed_addons';
  const HEADERS = { 'Content-Type': 'application/json' };

  // --- State ---
  let processing = false;
  let prevAddons = null;
  let prevMainIds = null;
  const systemRemoved = new Set();

  // ===========================================
  // MAIN HANDLER
  // ===========================================

  async function handleCartUpdate(cart) {
    if (processing) return;

    // Single pass: separate main items from addons
    const mainItems = [];
    const addonsInCart = [];

    for (const item of cart.items) {
      if (item.properties?._target_addon_product_id) {
        addonsInCart.push(item);
      } else {
        mainItems.push(item);
      }
    }

    const mainIds = new Set(mainItems.map(i => String(i.product_id)));

    // Track user-dismissed addons (removed manually)
    detectDismissed(addonsInCart, mainIds);
    prevAddons = addonsInCart.map(a => a.properties._target_addon_product_id);

    // Clear dismissed when main product removed
    clearDismissedOnRemoval(mainIds);
    prevMainIds = mainIds;

    // No products to process
    const handles = mainItems.map(i => i.handle).filter(Boolean);
    if (!handles.length && !addonsInCart.length) return;

    // Fetch addon metafields
    const addonData = await fetchAddonData(handles);

    // Calculate cart operations
    const ops = calculateOperations(mainItems, addonsInCart, mainIds, addonData);
    if (!ops.remove.length && !ops.add.length && !ops.update.length) return;

    // Execute operations
    processing = true;
    try {
      await executeOperations(ops);
      await refreshCart();
    } finally {
      processing = false;
    }
  }

  // ===========================================
  // CART OPERATIONS
  // ===========================================

  function calculateOperations(mainItems, addonsInCart, mainIds, addonData) {
    const remove = [];
    const add = [];
    const update = [];

    // Find orphaned addons (main product removed)
    for (const addon of addonsInCart) {
      const targetId = String(addon.properties._target_addon_product_id);
      if (!mainIds.has(targetId)) {
        remove.push(addon.key);
        systemRemoved.add(targetId);
      }
    }

    // Check each main product for required addon
    for (const item of mainItems) {
      const data = addonData[item.handle];
      if (!data?.first_available_addon) continue;
      if (isAddonDismissed(item.product_id)) continue;

      const productId = String(item.product_id);
      const existing = addonsInCart.find(
        a => String(a.properties._target_addon_product_id) === productId
      );

      if (!existing) {
        add.push({
          id: data.first_available_addon.variant_id,
          quantity: item.quantity,
          properties: { _target_addon_product_id: productId }
        });
      } else if (existing.quantity !== item.quantity) {
        update.push({ key: existing.key, quantity: item.quantity });
      }
    }

    return { remove, add, update };
  }

  async function executeOperations({ remove, add, update }) {
    // Remove orphaned addons
    if (remove.length) {
      await Promise.all(remove.map(key => cartChange(key, 0)));
    }

    // Add new addons
    if (add.length) {
      await cartAdd(add);
      // Track for dismissal detection
      const newIds = add.map(i => i.properties._target_addon_product_id);
      prevAddons = [...(prevAddons || []), ...newIds];
    }

    // Update quantities
    if (update.length) {
      await Promise.all(update.map(({ key, quantity }) => cartChange(key, quantity)));
    }
  }

  // ===========================================
  // DISMISSAL TRACKING
  // ===========================================

  function detectDismissed(addonsInCart, mainIds) {
    if (!prevAddons) return;

    for (const targetId of prevAddons) {
      const id = String(targetId);

      // Skip system-removed addons
      if (systemRemoved.has(id)) {
        systemRemoved.delete(id);
        continue;
      }

      const stillExists = addonsInCart.some(
        a => String(a.properties._target_addon_product_id) === id
      );

      // User removed addon while main product still in cart
      if (!stillExists && mainIds.has(id)) {
        dismissAddon(id);
      }
    }
  }

  function clearDismissedOnRemoval(mainIds) {
    if (!prevMainIds) return;
    for (const id of prevMainIds) {
      if (!mainIds.has(id)) clearDismissed(id);
    }
  }

  // ===========================================
  // CART API
  // ===========================================

  function cartAdd(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ items })
    });
  }

  function cartChange(id, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: HEADERS,
      body: JSON.stringify({ id, quantity })
    });
  }

  async function refreshCart() {
    const wrapper = document.querySelector('[data-cart-section="wrapper"]');
    const sectionId = wrapper?.dataset.sectionId;
    if (!sectionId) return;

    try {
      const res = await fetch(`/?sections=${sectionId}`);
      const data = await res.json();
      const html = data[sectionId];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newInner = doc.querySelector('[data-cart-section="inner"]');
      const currentInner = wrapper.querySelector('[data-cart-section="inner"]');

      if (newInner && currentInner) {
        currentInner.replaceWith(newInner);
      }
    } catch (e) {
      console.error('Cart refresh failed:', e);
    }
  }

  // ===========================================
  // DATA FETCHING
  // ===========================================

  async function fetchAddonData(handles) {
    if (!handles?.length) return {};

    const unique = [...new Set(handles)];
    const result = {};
    const toFetch = [];

    // Check cache first
    for (const handle of unique) {
      const cached = getCache(handle);
      if (cached) {
        result[handle] = cached;
      } else {
        toFetch.push(handle);
      }
    }

    // Fetch missing data in parallel
    if (toFetch.length) {
      const fetched = await fetchMetafields(toFetch);
      for (const handle of toFetch) {
        if (fetched[handle]) {
          const data = {
            first_available_addon: fetched[handle].first_available_addon,
            addons_discount: fetched[handle].addons_discount
          };
          setCache(handle, data);
          result[handle] = data;
        }
      }
    }

    return result;
  }

  async function fetchMetafields(handles) {
    const results = {};

    await Promise.all(handles.map(async handle => {
      try {
        const res = await fetch(`/products/${handle}?sections=product-metafields-api`);
        const data = await res.json();
        const html = data['product-metafields-api'];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const script = doc.querySelector('[data-product-metafields]');
        if (script) results[handle] = JSON.parse(script.textContent);
      } catch (e) {
        console.error(`Metafield fetch failed: ${handle}`, e);
      }
    }));

    return results;
  }

  // ===========================================
  // CACHE (localStorage)
  // ===========================================

  function getCache(handle) {
    try {
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
      const entry = cache[handle];
      if (!entry || Date.now() - entry.ts > CACHE_TTL) return null;
      return entry.data;
    } catch {
      return null;
    }
  }

  function setCache(handle, data) {
    try {
      const cache = JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
      cache[handle] = { data, ts: Date.now() };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch {}
  }

  // ===========================================
  // DISMISSED STATE (sessionStorage)
  // ===========================================

  function getDismissed() {
    try {
      return JSON.parse(sessionStorage.getItem(DISMISSED_KEY)) || {};
    } catch {
      return {};
    }
  }

  function isAddonDismissed(productId) {
    return !!getDismissed()[String(productId)];
  }

  function dismissAddon(productId) {
    try {
      const d = getDismissed();
      d[String(productId)] = true;
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(d));
    } catch {}
  }

  function clearDismissed(productId) {
    try {
      const d = getDismissed();
      delete d[String(productId)];
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(d));
    } catch {}
  }

  // ===========================================
  // EXPORT
  // ===========================================

  window.addonCartEvents = handleCartUpdate;

})();